import { isApiError } from "@sagot/api-client";
import { consoleRoutes, planAtLeast, WORKFLOWS_MIN_PLAN, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Dialog, DialogContent, EmptyState, ErrorState, Icon, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, toast } from "@sagot/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../session/workspace-context";
import { AnalysisPanel } from "./analysis/analysis-panel";
import { RefsProvider } from "./builder/refs";
import { HistoryContext } from "./builder/history-context";
import { SetupChecklist } from "./builder/setup-checklist";
import { DefaultWorkflowContext, type DefaultWorkflowInfo } from "./builder/system/default-context";
import { WorkflowBuilder } from "./builder/workflow-builder";
import { WorkflowIdContext } from "./builder/workflow-id-context";
import { CompareDefaultDialog } from "./defaults/compare-default-dialog";
import { DefaultBanner } from "./defaults/default-banner";
import { moduleOfTrigger, useWorkflowModules } from "./service/module-model";
import { DuplicateDialog } from "./defaults/duplicate-dialog";
import { PauseDefaultDialog } from "./defaults/pause-default-dialog";
import { RestoreDefaultDialog } from "./defaults/restore-default-dialog";
import { UpgradeSheet } from "./defaults/upgrade-prompts";
import { definitionFrom, kindOf, problemsOf } from "./definition-model";
import { OverviewPanel } from "./overview-panel";
import { PreviewDrawer } from "./preview/preview-drawer";
import { RunsPanel } from "./runs-panel";
import { TranslationsDialog } from "./translations/translations-panel";
import type { WorkflowDetail } from "./types";
import { useEditorHistory } from "./use-editor-history";
import { useWorkflowProblems, type Refusal } from "./use-workflow-problems";
import { useWorkflow, useWorkflowMutations } from "./use-workflows";
import { PublishDialog } from "./versions/publish-dialog";
import { REGRESSION_NOT_PASSED } from "./versions/regression-model";
import { RegressionCasesPanel } from "./versions/regression-cases-panel";
import { VersionsPanel } from "./versions/versions-panel";
import { PUBLISH_HINT_ID, WorkflowHeader } from "./workflow-header";

const B = "engage.workflows.builder";

const TABS = ["build", "overview", "paths", "runs", "versions", "tests"] as const;
type Tab = (typeof TABS)[number];
/** The dialogs a default workflow's actions open from the builder. */
type DefaultDialogKind = "pause" | "restore" | "compare" | "duplicate" | "upgrade";
/** A workflow that does not exist (or belongs to another workspace) answers 404; a malformed id 422. */
const NOT_FOUND_STATUSES: ReadonlySet<number> = new Set([404, 422]);

function Editor({ workflow, tab }: { workflow: WorkflowDetail; tab: Tab }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const canManage = permissions.can("workflows.manage") && !permissions.readOnly;
  const canPublish = permissions.can("workflows.publish") && !permissions.readOnly;
  const mutations = useWorkflowMutations(workflow.id);
  // The version of the workflow this editor last saw (opened, saved, published): a save from a stale tab is refused, not merged silently.
  const seen = useRef(workflow.updated_at);
  const [name, setName] = useState(workflow.name);
  const [dirty, setDirty] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const edits = useEditorHistory({ initial: () => definitionFrom(workflow.draft), keys: tab === "build" && canManage, onEdited: () => { setDirty(true); } });
  const { definition, change } = edits;
  const plan = useWorkspaceDetails().data?.plan;
  const flagged = useWorkflowProblems({ definition, trigger: workflow.trigger as WorkflowTrigger, plan, refusal });
  const [deleting, setDeleting] = useState(false);
  const [defaultDialog, setDefaultDialog] = useState<DefaultDialogKind | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [translating, setTranslating] = useState(false);
  /** Why publishing is held by the test cases (the dialog is open); `null`: not held. */
  const [held, setHeld] = useState<string | null>(null);
  const kind = kindOf(definition);
  const isDefault = workflow.origin === "system";
  // A default of a Service module that is switched off was paused by that switch: the banner says so.
  const modules = useWorkflowModules();
  const needed = moduleOfTrigger(workflow.trigger);
  const moduleOff = needed !== undefined && modules.known && modules.isOff(needed) ? needed : null;
  const canDuplicate = plan === undefined || planAtLeast(plan, WORKFLOWS_MIN_PLAN);
  const defaultInfo: DefaultWorkflowInfo | null = isDefault ? { systemKey: workflow.system_key ?? null, execution: workflow.execution, canDuplicate } : null;
  const canPreview = canManage && (kind === "background" || permissions.can("playground.use", { anywhere: true }));
  const busy = mutations.save.isPending || mutations.publish.isPending;
  // A tab click fires "change" on mouse-down and again on focus, before the route has re-rendered: one history entry per tab, so Back returns to the previous tab.
  const requestedTab = useRef<string | null>(null);
  useEffect(() => { requestedTab.current = null; }, [tab]);
  const openTab = (next: string) => {
    if (next === tab || next === requestedTab.current) return;
    requestedTab.current = next;
    void navigate(consoleRoutes.workflow(summary.slug, workflow.id, next === "build" ? undefined : next));
  };

  const onFailure = (error: unknown) => {
    const found = isApiError(error) ? problemsOf(error.details.problems) : [];
    setProblems(found);
    setRefusal(found.length > 0 ? { lines: found, definition } : null);
    toast.error(message(error));
  };
  const save = async (): Promise<boolean> => {
    try {
      const saved = await mutations.save.mutateAsync({ name: name.trim() || workflow.name, definition, base_updated_at: seen.current });
      seen.current = saved.updated_at;
      setDirty(false);
      setProblems([]);
      setRefusal(null);
      return true;
    } catch (error) {
      onFailure(error);
      return false;
    }
  };
  const publish = async () => {
    if (dirty && !(await save())) return;
    mutations.publish.mutate(undefined, {
      onSuccess: (done) => { seen.current = done.updated_at; setProblems([]); setRefusal(null); toast.success(t(`${B}.published`, { version: done.live_version ?? 1 })); },
      onError: (error) => {
        if (isApiError(error) && error.code === REGRESSION_NOT_PASSED) setHeld(typeof error.details.reason === "string" ? error.details.reason : "");
        else onFailure(error);
      },
    });
  };
  /** An older version now sits in the draft: the builder shows it, with nothing unsaved and nothing to undo. */
  const rolledBack = (restored: WorkflowDetail) => {
    seen.current = restored.updated_at;
    edits.reset(definitionFrom(restored.draft));
    setDirty(false);
    setProblems([]);
    setRefusal(null);
    void navigate(consoleRoutes.workflow(summary.slug, workflow.id));
  };
  const pauseNow = () => {
    mutations.pause.mutate(undefined, { onSuccess: (done) => { seen.current = done.updated_at; setDefaultDialog(null); toast.info(t(`${B}.paused`)); }, onError: onFailure });
  };
  // The dialogs the More menu opens hand focus back to it (the menu item that opened them is gone by then).
  const moreRef = useRef<HTMLButtonElement>(null);
  const returnToMore = (event: Event) => {
    event.preventDefault();
    moreRef.current?.focus();
  };
  return (
    <DefaultWorkflowContext.Provider value={defaultInfo}>
    <div className="space-y-5">
      <WorkflowHeader
        workflow={workflow} kind={kind} name={name} dirty={dirty} canManage={canManage} canPublish={canPublish} canPreview={canPreview} busy={busy} problemCount={flagged.all.length}
        onPreview={() => { setPreviewing(true); }}
        onName={(value) => { setName(value); setDirty(true); }}
        onSave={() => void save().then((ok) => { if (ok) toast.success(t(`${B}.saved`)); })}
        onPublish={() => void publish()}
        onPause={() => { if (isDefault) setDefaultDialog("pause"); else pauseNow(); }}
        onResume={() => { mutations.resume.mutate(undefined, { onSuccess: (done) => { seen.current = done.updated_at; toast.success(t(`${B}.resumed`)); }, onError: onFailure }); }}
        onDelete={() => { setDeleting(true); }}
        onTranslations={() => { setTranslating(true); }}
        moreRef={moreRef}
        defaults={isDefault ? { canDuplicate, onRestore: () => { setDefaultDialog("restore"); }, onCompare: () => { setDefaultDialog("compare"); }, onDuplicate: () => { setDefaultDialog(canDuplicate ? "duplicate" : "upgrade"); } } : undefined}
      />
      {isDefault ? (
        <DefaultBanner workflow={workflow} canPublish={canPublish} canDuplicate={canDuplicate} moduleOff={moduleOff} onRestore={() => { setDefaultDialog("restore"); }} onCompare={() => { setDefaultDialog("compare"); }} />
      ) : null}
      {!canManage ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${B}.readOnly`)}</p> : null}
      {canManage && !canPublish ? <p id={PUBLISH_HINT_ID} className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${B}.cannotPublish`)}</p> : null}
      {dirty ? <p className="text-xs font-semibold text-fg-muted" role="status">{t(`${B}.unsaved`)}</p> : null}
      {problems.length > 0 ? (
        <div role="alert" className="rounded-md bg-warning-soft p-3 text-sm">
          <p className="font-semibold">{t(`${B}.problems`)}</p>
          <ul className="mt-1 list-disc pl-5 text-xs">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      ) : null}
      <Tabs value={tab} onValueChange={openTab}>
        <TabsList className="flex overflow-x-auto overflow-y-hidden" aria-label={t("engage.workflows.title")}>
          {TABS.filter((key) => key !== "tests" || kind === "facing").map((key) => <TabsTrigger key={key} value={key}>{t(`${B}.tabs.${key}`)}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="build" className="space-y-5 pt-4">
          <WorkflowIdContext.Provider value={workflow.id}>
            <HistoryContext.Provider value={edits.api}>
              <SetupChecklist workflowId={workflow.id} workflowName={workflow.name} />
              <WorkflowBuilder trigger={workflow.trigger as WorkflowTrigger} definition={definition} disabled={!canManage} problems={flagged} onChange={change} />
            </HistoryContext.Provider>
          </WorkflowIdContext.Provider>
        </TabsContent>
        <TabsContent value="overview" className="pt-4">{tab === "overview" ? <OverviewPanel workflowId={workflow.id} goal={workflow.goal ?? null} /> : null}</TabsContent>
        <TabsContent value="paths" className="pt-4">{tab === "paths" ? <AnalysisPanel workflow={workflow} definition={definition} /> : null}</TabsContent>
        <TabsContent value="runs" className="pt-4">{tab === "runs" ? <RunsPanel workflowId={workflow.id} /> : null}</TabsContent>
        <TabsContent value="versions" className="pt-4">
          {tab === "versions" ? <VersionsPanel workflow={workflow} trigger={workflow.trigger as WorkflowTrigger} dirty={dirty} canManage={canManage} onRolledBack={rolledBack} /> : null}
        </TabsContent>
        <TabsContent value="tests" className="pt-4">{tab === "tests" ? <RegressionCasesPanel workflowId={workflow.id} canManage={canManage} /> : null}</TabsContent>
      </Tabs>
      {held !== null ? (
        <PublishDialog
          workflowId={workflow.id} reason={held || null} canOverride={permissions.can("workflows.publish_override")}
          onPublished={(done) => { seen.current = done.updated_at; setHeld(null); setProblems([]); setRefusal(null); }}
          onClose={() => { setHeld(null); }}
        />
      ) : null}
      <TranslationsDialog open={translating} onOpenChange={setTranslating} onCloseAutoFocus={returnToMore} workflowId={workflow.id} definition={definition} disabled={!canManage} onChange={change} onDraftApplied={edits.resetToLatest} />
      {previewing ? <PreviewDrawer workflowId={workflow.id} definition={definition} facing={kind === "facing"} onClose={() => { setPreviewing(false); }} /> : null}
      {defaultDialog === "pause" ? (
        <PauseDefaultDialog workflow={workflow} busy={mutations.pause.isPending} onConfirm={pauseNow} onClose={() => { setDefaultDialog(null); }} onCloseAutoFocus={returnToMore} />
      ) : null}
      {defaultDialog === "restore" ? (
        <RestoreDefaultDialog workflow={workflow} unsaved={dirty} onRestored={rolledBack} onClose={() => { setDefaultDialog(null); }} onCloseAutoFocus={returnToMore} />
      ) : null}
      {defaultDialog === "compare" ? <CompareDefaultDialog workflowId={workflow.id} unsaved={dirty} onClose={() => { setDefaultDialog(null); }} onCloseAutoFocus={returnToMore} /> : null}
      {defaultDialog === "duplicate" ? <DuplicateDialog workflow={workflow} onClose={() => { setDefaultDialog(null); }} onCloseAutoFocus={returnToMore} /> : null}
      {defaultDialog === "upgrade" ? <UpgradeSheet onClose={() => { setDefaultDialog(null); }} onCloseAutoFocus={returnToMore} /> : null}
      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent
          title={t(`${B}.deleteTitle`, { name: workflow.name })} description={t(`${B}.deleteBody`)} closeLabel={t("common.close")} onCloseAutoFocus={returnToMore}
          footer={<>
            <Button variant="secondary" onClick={() => { setDeleting(false); }}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={mutations.remove.isPending} onClick={() => {
              mutations.remove.mutate(undefined, { onSuccess: () => { toast.success(t(`${B}.deleted`)); void navigate(consoleRoutes.workflows(summary.slug)); }, onError: onFailure });
            }}>{t(`${B}.delete`)}</Button>
          </>}
        />
      </Dialog>
    </div>
    </DefaultWorkflowContext.Provider>
  );
}

/** `/w/:ws/workflows/:workflowId/:tab?` — build, runs and overview of one workflow. */
export function WorkflowPage() {
  const { t } = useTranslation();
  const params = useParams();
  const id = params.workflowId ?? "";
  const tab: Tab = TABS.find((key) => key === params.tab) ?? "build";
  const workflow = useWorkflow(id);
  const { summary } = useWorkspaceContext();
  if (workflow.isError && isApiError(workflow.error) && NOT_FOUND_STATUSES.has(workflow.error.status)) {
    return (
      <div className="mx-auto max-w-6xl" data-testid="workflow-not-found">
        <EmptyState
          icon="workflow"
          title={t("engage.workflows.notFound.title")}
          description={t("engage.workflows.notFound.body")}
          action={<Button asChild variant="secondary"><AppLink href={consoleRoutes.workflows(summary.slug)}><Icon name="arrow-left" />{t(`${B}.back`)}</AppLink></Button>}
        />
      </div>
    );
  }
  if (workflow.isError) return <ErrorState title={t("engage.workflows.loadFailed")} onRetry={() => void workflow.refetch()} retryLabel={t("common.retry")} />;
  if (workflow.isPending) return <Skeleton className="h-96 rounded-card" />;
  return (
    <div className="mx-auto max-w-6xl">
      <RefsProvider selfId={workflow.data.id}>
        <Editor key={workflow.data.id} workflow={workflow.data} tab={tab} />
      </RefsProvider>
    </div>
  );
}
